import { DateTime } from 'luxon';
import type { TFunction } from 'i18next';

import { formatChronicleDate, formatDate, formatMoney, formatNumber } from '@/utils/format';

export interface ReportExportColumn {
  key: string;
  type: 'string' | 'number' | 'money' | 'date';
}

const ENUM_VALUE_COLUMNS = new Set([
  'value',
  'status',
  'memberType',
  'gender',
  'contributionType',
  'method',
  'lineType',
  'isLeader',
  'isOpen',
  'reason',
  'requestType',
  'channel',
  'entryType',
  'role',
  'baptismType',
  'category',
  'failureCode',
  'action',
  'entityType',
  'actorRole',
  'programKind',
  'visibility',
  'datePrecision',
]);

const SNAKE_CASE = /^[a-z][a-z0-9]*(_[a-z0-9]+)+$/;
const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-7][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

const looksLikeOpaqueId = (columnKey: string, text: string): boolean =>
  (columnKey === 'id' || columnKey.endsWith('Id')) && UUID_PATTERN.test(text);

export const humanizeReportValue = (value: string): string =>
  value
    .split(/[._]/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

export const translateReportValue = (t: TFunction<'reports'>, value: string): string => {
  const translated = t(`value.${value}`, { defaultValue: '' });
  if (translated) return translated;
  return humanizeReportValue(value);
};

export const reportRowIsLiability = (row: Record<string, unknown>): boolean =>
  row.section === 'liabilities';

export const formatReportCellValue = (
  column: ReportExportColumn,
  value: unknown,
  row: Record<string, unknown>,
  t: TFunction<'reports'>,
  options: { locale?: string } = {},
): string => {
  if (value === null || value === undefined || value === '') return '—';

  if (column.type === 'money') {
    const currency = typeof row.currency === 'string' ? row.currency : 'GHS';
    return formatMoney(Number(value), currency, {
      locale: options.locale,
      parenthesiseNegative: true,
    });
  }

  if (column.type === 'number') {
    return formatNumber(Number(value), options.locale);
  }

  if (column.type === 'date') {
    if (
      column.key === 'occurredOn' &&
      (row.datePrecision === 'day' ||
        row.datePrecision === 'month' ||
        row.datePrecision === 'year')
    ) {
      return formatChronicleDate(String(value), row.datePrecision, { locale: options.locale });
    }
    return formatDate(String(value), { locale: options.locale });
  }

  const text = String(value);

  if (looksLikeOpaqueId(column.key, text)) return '—';

  if (column.key === 'dimension') return t(`dimension.${text}`, text);
  if (column.key === 'section') return t(`section.${text}`, text);
  if (column.key === 'lineLabel') {
    const label = t(`line.${text}`, text);
    return row.indent === true ? `  ${label}` : label;
  }

  if (column.key === 'month' && /^\d{4}-\d{2}$/.test(text)) {
    const parsed = DateTime.fromISO(`${text}-01`);
    if (parsed.isValid) {
      return parsed.setLocale(options.locale ?? 'en').toLocaleString({ month: 'long', year: 'numeric' });
    }
  }

  if (ENUM_VALUE_COLUMNS.has(column.key) || SNAKE_CASE.test(text)) {
    return translateReportValue(t, text);
  }

  return text;
};

export const formatReportExportCell = (
  column: ReportExportColumn,
  value: unknown,
  row: Record<string, unknown>,
  t: TFunction<'reports'>,
  options: { locale?: string } = {},
): string => formatReportCellValue(column, value, row, t, options);

export const visibleExportColumns = (
  columns: ReportExportColumn[],
  rows: Record<string, unknown>[],
): ReportExportColumn[] => {
  if (rows.length === 0) return columns;
  const currencyColumn = columns.find((column) => column.key === 'currency');
  if (!currencyColumn) return columns;
  const currencies = new Set(
    rows
      .map((row) => row.currency)
      .filter((value): value is string => typeof value === 'string' && value.length > 0),
  );
  if (currencies.size <= 1) {
    return columns.filter((column) => column.key !== 'currency');
  }
  return columns;
};

export const formatReportTotalsLines = (
  totals: Record<string, unknown> | undefined,
  t: TFunction<'reports'>,
  baseCurrency: string,
): string[] => {
  if (!totals) return [];
  const currency = typeof totals.currency === 'string' ? totals.currency : baseCurrency;
  const lines: string[] = [];

  if (totals.revenueMinor !== undefined) {
    lines.push(
      `${t('totals.revenue')}: ${formatMoney(Number(totals.revenueMinor), currency, { parenthesiseNegative: true })}`,
      `${t('totals.expenses')}: ${formatMoney(Number(totals.expenseMinor ?? 0), currency, { parenthesiseNegative: true })}`,
      `${t('totals.surplus')}: ${formatMoney(Number(totals.surplusMinor ?? 0), currency, { parenthesiseNegative: true })}`,
    );
  } else if (totals.totalAssetsMinor !== undefined) {
    lines.push(
      `${t('totals.totalAssets')}: ${formatMoney(Number(totals.totalAssetsMinor), currency, { parenthesiseNegative: true })}`,
      `${t('totals.totalLiabilities')}: ${formatMoney(Number(totals.totalLiabilitiesMinor ?? 0), currency, { parenthesiseNegative: true })}`,
      `${t('totals.totalNetAssets')}: ${formatMoney(Number(totals.totalNetAssetsMinor ?? 0), currency, { parenthesiseNegative: true })}`,
    );
  } else if (totals.amountMinor !== undefined) {
    lines.push(
      `${t('column.amountMinor')}: ${formatMoney(Number(totals.amountMinor), currency, { parenthesiseNegative: true })}`,
    );
  }

  return lines;
};
